<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import dayjs from 'dayjs';
import DoNotContactChip from '@/components/Common/DoNotContactChip.vue';
import DoNotContactModal from './DoNotContactModal.vue';
import DoNotContactService, {
  type DoNotContactSource,
} from '@/core/conversations/do-not-contact/do-not-contact.service';
import type { DoNotContactSummary } from '@/core/conversations/do-not-contact/do-not-contact.type';
import { ErrorService } from '@/core/shared/errors/error.service';

const props = defineProps<{
  employerId: number;
  userProfileId: number;
  candidateName: string;
  source: DoNotContactSource;
}>();
const emit = defineEmits<{
  (event: 'removed', employerId: number, userProfileId: number): void;
}>();
const service = new DoNotContactService();
const isOpen = ref(false);
const isLoading = ref(false);
const loadFailed = ref(false);
const summary = ref<DoNotContactSummary | null>(null);
const showConfirmation = ref(false);
const isSubmitting = ref(false);
let requestVersion = 0;
let isMounted = true;
let confirmationTarget: { employerId: number; userProfileId: number } | null = null;

const actor = computed(() =>
  [summary.value?.createdBy?.nameFirst, summary.value?.createdBy?.nameLast]
    .filter(Boolean)
    .join(' ')
    .trim(),
);
const date = computed(() =>
  summary.value ? dayjs(summary.value.createTs).format('MMM D, YYYY') : '',
);

function isCurrentTarget(employerId: number, userProfileId: number) {
  return isMounted && props.employerId === employerId && props.userProfileId === userProfileId;
}

async function loadSummary() {
  const version = ++requestVersion;
  const { employerId, userProfileId } = props;
  summary.value = null;
  loadFailed.value = false;
  isLoading.value = true;
  try {
    const result = await service.getSummary(employerId, userProfileId);
    if (version !== requestVersion || !isCurrentTarget(employerId, userProfileId)) return;
    summary.value = result;
    if (result === null) emit('removed', employerId, userProfileId);
  } catch (error) {
    if (version !== requestVersion || !isCurrentTarget(employerId, userProfileId)) return;
    ErrorService.captureException(error);
    loadFailed.value = true;
  } finally {
    if (version === requestVersion) isLoading.value = false;
  }
}

watch(isOpen, (open) => {
  if (open) loadSummary();
  else {
    requestVersion++;
    summary.value = null;
  }
});
watch(
  () => [props.employerId, props.userProfileId],
  () => {
    requestVersion++;
    isOpen.value = false;
    summary.value = null;
    showConfirmation.value = false;
    confirmationTarget = null;
  },
);
onBeforeUnmount(() => {
  isMounted = false;
  requestVersion++;
});

function confirmUndo() {
  confirmationTarget = { employerId: props.employerId, userProfileId: props.userProfileId };
  isOpen.value = false;
  showConfirmation.value = true;
}

async function undo() {
  const target = confirmationTarget;
  if (isSubmitting.value || !target || !isCurrentTarget(target.employerId, target.userProfileId))
    return;
  isSubmitting.value = true;
  try {
    const succeeded = await service.unmarkPro(
      target.employerId,
      target.userProfileId,
      props.source,
    );
    if (!succeeded || !isCurrentTarget(target.employerId, target.userProfileId)) return;
    showConfirmation.value = false;
    emit('removed', target.employerId, target.userProfileId);
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <span class="inline-flex" @click.stop>
    <!-- Keep the visible gap inside the menu's hit area so the pointer can reach Undo. -->
    <v-menu
      v-model="isOpen"
      location="bottom start"
      :offset="0"
      content-class="py-2"
      :width="320"
      max-width="calc(100vw - 32px)"
      :open-on-click="false"
      :open-on-hover="true"
      :open-on-focus="false"
      :open-delay="200"
      :close-delay="500"
      transition="fade-transition"
      :close-on-content-click="false"
      :disabled="showConfirmation"
    >
      <template #activator="{ props: activatorProps }">
        <button
          v-bind="activatorProps"
          type="button"
          data-test="do-not-contact-details"
          :aria-label="`Do Not Contact details for ${candidateName}`"
          class="rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-highlight-500"
          @click="isOpen = true"
        >
          <DoNotContactChip />
        </button>
      </template>
      <div
        data-test="do-not-contact-popover"
        class="menu-scrollbar flex min-h-0 flex-col items-end gap-2 overflow-y-auto break-words rounded-xl border border-tint-80 bg-white p-4 shadow-[0px_8px_16px_0px_rgba(5,13,29,0.16)]"
        @click.stop
      >
        <p v-if="isLoading" role="status" class="w-full text-xs text-shade-800">Loading details…</p>
        <div v-else-if="loadFailed" class="w-full text-xs text-shade-820">
          <p role="alert">Could not load Do Not Contact details.</p>
          <button
            type="button"
            class="mt-2 font-semibold underline"
            data-test="do-not-contact-retry"
            @click="loadSummary"
          >
            Try again
          </button>
        </div>
        <template v-else-if="summary">
          <p class="w-full text-xs leading-[18px] text-[#738092]">
            Marked<span v-if="actor"> by {{ actor }}</span> · {{ date }}
          </p>
          <div v-if="summary.reason" class="flex w-full flex-col gap-1 rounded-lg bg-tint-20 p-2">
            <p class="text-[11px] font-bold leading-4 text-[#738092]">Reason</p>
            <p class="whitespace-pre-wrap text-[13px] leading-5 text-shade-820">
              {{ summary.reason }}
            </p>
          </div>
        </template>
        <button
          type="button"
          data-test="do-not-contact-popover-undo"
          class="min-h-11 rounded-md px-4 py-2.5 text-sm font-semibold leading-5 text-shade-880 hover:bg-tint-40 sm:min-h-10"
          @click="confirmUndo"
        >
          Undo Do Not Contact
        </button>
      </div>
    </v-menu>
    <DoNotContactModal
      v-model="showConfirmation"
      variant="remove"
      :candidate-name="candidateName"
      :is-loading="isSubmitting"
      @submit="undo"
    />
  </span>
</template>
